import React, { useEffect, useState, useRef, useCallback } from 'react';
import { 
  Grid, 
  Box, 
  Typography, 
  Divider, 
  LinearProgress, 
  IconButton, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper
} from '@mui/material';
import { styled } from '@mui/system';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import SchoolIcon from '@mui/icons-material/School';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

import {
  ChevronLeft,
  ChevronRight,
  DragIndicator,
  Reorder
} from "@mui/icons-material";

// --- Styled Components for Consistent Theming ---

const HeaderBackground = styled(Box)(({ color }) => ({
  backgroundColor: color || '#f0f0f0',
  color: '#333',
  padding: '30px 40px',
  textAlign: 'center',
  marginBottom: '30px',
}));

const NameTitle = styled(Typography)(({ color, font, fontSize }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 800,
  fontSize: fontSize || '2.5rem',
  lineHeight: 1,
  textTransform: 'uppercase',
  color: color || '#333',
}));

const RoleTitle = styled(Typography)(({ color, font, fontSize }) => ({
  fontFamily: font || 'Roboto, sans-serif',
  fontWeight: 400,
  fontSize: fontSize || '1rem',
  textTransform: 'uppercase',
  letterSpacing: '2px',
  marginTop: '5px',
  color: color || '#555',
}));

const SidebarSectionHeader = styled('h3')(({ color, font, fontSize }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 700,
  fontSize: fontSize || '1.1rem',
  color: color || '#333',
  textTransform: 'uppercase',
  margin: 0,
  marginBottom: '15px',
  paddingBottom: '5px',
  borderBottom: `1px solid ${color ? 'rgba(0,0,0,0.1)' : '#ddd'}`,
}));

const MainContentSectionHeader = styled('h3')(({ color, font, fontSize }) => ({
  fontFamily: font || 'Montserrat, sans-serif',
  fontWeight: 700,
  fontSize: fontSize || '1.1rem',
  color: color || '#333',
  textTransform: 'uppercase',
  margin: 0,
  marginBottom: '15px',
  paddingBottom: '5px',
  borderBottom: `1px solid ${color ? 'rgba(0,0,0,0.1)' : '#ddd'}`,
}));

const InfoItem = styled(Box)(({ color, font, fontSize, fontStyle, lineSpacing }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '8px',
  fontFamily: font || 'Arial, sans-serif',
  fontSize: fontSize || '14px',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineSpacing ? `${lineSpacing}px` : 'normal',
  '& .MuiSvgIcon-root': {
    fontSize: '1.1rem',
    marginRight: '10px',
    color: color || '#777',
    flexShrink: 0,
  },
  '& .MuiTypography-root': {
    color: '#555',
    fontFamily: font || 'Roboto, sans-serif',
    fontSize: fontSize || '0.9rem',
    fontStyle: fontStyle || 'normal',
    lineHeight: lineSpacing ? `${lineSpacing}px` : 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'break-word',
    minWidth: 0,
    flex: 1,
  }
}));

const SkillBarWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '15px',
});

const SkillLabel = styled(Typography)(({ color, font, fontSize, fontStyle, lineSpacing }) => ({
  fontFamily: font || 'Roboto, sans-serif',
  fontWeight: 600,
  color: color || '#333',
  textTransform: 'uppercase',
  marginBottom: '5px',
  fontSize: fontSize || '0.9rem',
  fontStyle: fontStyle || 'normal',
  lineHeight: lineSpacing ? `${lineSpacing}px` : 'normal',
}));

const SkillBar = styled(LinearProgress)(({ color }) => ({
  height: 8,
  borderRadius: 4,
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: color || '#777',
  },
}));

const TimelineItem = styled(Box)({
  display: 'flex',
  position: 'relative',
  marginBottom: '20px',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: '8px',
    top: '0',
    bottom: '0',
    width: '2px',
    backgroundColor: '#ddd',
  },
});

const TimelineIcon = styled(Box)(({ color }) => ({
  backgroundColor: color || '#777',
  borderRadius: '50%',
  width: '18px',
  height: '18px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
  color: '#fff',
  marginLeft: '0px',
  marginRight: '20px',
  flexShrink: 0,
  '& .MuiSvgIcon-root': {
    fontSize: '0.9rem',
  },
}));

// Page size mapping
const pageSizeMap = {
  A4: { width: "210mm", height: "297mm" },
  Letter: { width: "216mm", height: "279mm" },
  Legal: { width: "216mm", height: "356mm" },
  A3: { width: "297mm", height: "420mm" },
  Executive: { width: "184mm", height: "267mm" },
};

// Default data
const defaultData = {
  firstName: "John",
  lastName: "Smith",
  currentPosition: "Copywriter",
  phone: "123-456-7890",
  email: "your@email.com",
  city: "1234 Main Street, Your City 20200",
  website: "www.yourcompany.com",
  profile: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum do",
};

// Separate section orders for left and right sides
const defaultLeftSectionOrder = [
  'profile',
  'education',
  'skills'
];

const defaultRightSectionOrder = [
  'aboutMe',
  'experience',
  'languages',
  'accomplishments',
  'certifications',
  'softwareSkills',
  'volunteering',
  'interests',
  'websites', 
  'references',
  'additionalInfo'
];

// --- Resume Component ---

const Resume21 = ({
  color = '#f0f0f0',
  nameColor = '#333',
  sidebarBackground = '#fff',
  headerBackground = '#f0f0f0',
  accentColor = '#777',
  font = 'Arial, sans-serif',
  fontSize = '14px',
  fontStyle = 'normal',
  headingSize = '24px',
  sectionSpacing = 30,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 30,
  sideMargins = 20,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = 'A4',
  theme,
  formData = {},
  workExperiences = [],
  exporting = false,
  enableDragDrop = true,
  linkedIn = '',
}) => {
  const primaryColor = theme?.primary || color;
  
  const data = { 
    ...defaultData, 
    ...formData,
    linkedIn: linkedIn || formData.linkedIn || '',
  };
  
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const rightContentRef = useRef(null);

  const [leftSectionOrder, setLeftSectionOrder] = useState(defaultLeftSectionOrder);
  const [rightSectionOrder, setRightSectionOrder] = useState(defaultRightSectionOrder);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const palette = {
    mainBackground: '#FFFFFF',
    sidebarBackground: sidebarBackground,
    accentColor: primaryColor,
    textColor: '#333333',
    lightText: '#666666',
    header: headerBackground,
    nameColor: nameColor,
    sidebarText: '#555',
  };

  const globalFontStyle = {
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`,
  };

  const getTextStyle = (additionalStyles = {}) => ({
    ...globalFontStyle,
    ...additionalStyles
  });

  const getFontSize = (multiplier = 1) => {
    const baseSize = parseInt(fontSize) || 14;
    return `${baseSize * multiplier}px`;
  };

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  // Load data from localStorage
  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));
    
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));
    
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);
    
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);
    
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);
    
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);
    
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);
    
    const userId = localStorage.getItem("userId");
    console.log("👤 Current userId:", userId);
    
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      console.log("🔍 Current custom sections to display:", currentCustomSections);
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
                console.log(`✅ Loaded current custom section: ${sectionName} with ${validItems.length} items`);
              }
            }
          } catch (error) {
            console.error(`❌ Error loading current custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    const savedLeftSectionOrder = localStorage.getItem("resume21LeftSectionOrder");
    if (savedLeftSectionOrder) {
      const parsedOrder = JSON.parse(savedLeftSectionOrder);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      setLeftSectionOrder(filteredOrder);
    } else {
      setLeftSectionOrder(defaultLeftSectionOrder);
    }

    const savedRightSectionOrder = localStorage.getItem("resume21RightSectionOrder");
    if (savedRightSectionOrder) {
      const parsedOrder = JSON.parse(savedRightSectionOrder);
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allRightSections = [...filteredOrder, ...currentCustomSectionIds];
      setRightSectionOrder(allRightSections);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedRightSectionOrder = [...defaultRightSectionOrder, ...currentCustomSectionIds];
      setRightSectionOrder(updatedRightSectionOrder);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredRightOrder = rightSectionOrder.filter(section => !section.startsWith('custom_'));
      const newRightOrder = [...filteredRightOrder, ...currentCustomSectionIds];
      if (JSON.stringify(newRightOrder) !== JSON.stringify(rightSectionOrder)) {
        setRightSectionOrder(newRightOrder);
        localStorage.setItem("resume21RightSectionOrder", JSON.stringify(newRightOrder));
      }
      const filteredLeftOrder = leftSectionOrder.filter(section => !section.startsWith('custom_'));
      if (JSON.stringify(filteredLeftOrder) !== JSON.stringify(leftSectionOrder)) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume21LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const filteredRightOrder = rightSectionOrder.filter(section => !section.startsWith('custom_'));
      if (filteredRightOrder.length !== rightSectionOrder.length) {
        setRightSectionOrder(filteredRightOrder);
        localStorage.setItem("resume21RightSectionOrder", JSON.stringify(filteredRightOrder));
      }
      const filteredLeftOrder = leftSectionOrder.filter(section => !section.startsWith('custom_'));
      if (filteredLeftOrder.length !== leftSectionOrder.length) {
        setLeftSectionOrder(filteredLeftOrder);
        localStorage.setItem("resume21LeftSectionOrder", JSON.stringify(filteredLeftOrder));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (leftSectionOrder.length > 0) {
      localStorage.setItem("resume21LeftSectionOrder", JSON.stringify(leftSectionOrder));
    }
  }, [leftSectionOrder]);

  useEffect(() => {
    if (rightSectionOrder.length > 0) {
      localStorage.setItem("resume21RightSectionOrder", JSON.stringify(rightSectionOrder));
    }
  }, [rightSectionOrder]);

  // Drag and drop handlers
  const handleLeftDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'left', sectionId: leftSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleRightDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, side: 'right', sectionId: rightSectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index, side) => {
    e.preventDefault();
    setDragOverIndex(index);
    setDragOverSide(side);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  const handleLeftDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'left' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...leftSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setLeftSectionOrder(newSectionOrder);
    } else if (sourceSide === 'right') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newRightSectionOrder.splice(sourceIndex, 1);
      newLeftSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleRightDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    const sourceSide = data.side;
    const sectionId = data.sectionId;

    if (sourceSide === 'right' && sourceIndex !== targetIndex) {
      const newSectionOrder = [...rightSectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setRightSectionOrder(newSectionOrder);
    } else if (sourceSide === 'left') {
      const newLeftSectionOrder = [...leftSectionOrder];
      const newRightSectionOrder = [...rightSectionOrder];
      newLeftSectionOrder.splice(sourceIndex, 1);
      newRightSectionOrder.splice(targetIndex, 0, sectionId);
      setLeftSectionOrder(newLeftSectionOrder);
      setRightSectionOrder(newRightSectionOrder);
    }
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    setDragOverSide(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetLeftSectionOrder = () => setLeftSectionOrder(defaultLeftSectionOrder);
  const resetRightSectionOrder = () => setRightSectionOrder(defaultRightSectionOrder);
  const resetAllSectionOrder = () => {
    setLeftSectionOrder(defaultLeftSectionOrder);
    setRightSectionOrder(defaultRightSectionOrder);
  };

  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const renderDragPanel = (side, sections) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        {side === 'left' ? 'Left Side' : 'Right Side'} Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          ({sections.length} sections)
        </Typography>
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sections.length > 0 ? (
          sections.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) => side === 'left' ? handleLeftDragStart(e, index) : handleRightDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index, side)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => side === 'left' ? handleLeftDrop(e, index) : handleRightDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2, mb: 1, display: 'flex', alignItems: 'center',
                backgroundColor: dragOverIndex === index && dragOverSide === side ? '#f0f0f0' : 'white',
                boxShadow: dragOverIndex === index && dragOverSide === side ? 2 : 1,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
                transition: 'all 0.2s ease',
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
              <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections. Drag sections from the other side.
          </Typography>
        )}
      </Box>
      <Button onClick={side === 'left' ? resetLeftSectionOrder : resetRightSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset {side === 'left' ? 'Left' : 'Right'} Order
      </Button>
    </Box>
  );

  const getSectionTitle = (sectionId) => {
    const titles = {
      'profile': 'Profile', 'education': 'Education', 'skills': 'Skills',
      'aboutMe': 'About Me', 'experience': 'Professional Experience',
      'languages': 'Languages', 'accomplishments': 'Accomplishments',
      'certifications': 'Certifications', 'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering', 'interests': 'Interests',
      'references': 'References', 'additionalInfo': 'Additional Information'
    };
    if (sectionId.startsWith('custom_')) return sectionId.replace('custom_', '').toUpperCase();
    return titles[sectionId] || sectionId;
  };

  const addFontStylesToHTML = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent
      .replace(/<div/g, `<div style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${fontSize}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; max-width: 100%;"`)
      .replace(/<p/g, `<p style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${fontSize}; line-height: ${lineSpacing}px; word-break: break-word; overflow-wrap: break-word; text-indent: ${paragraphIndent}px; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<span/g, `<span style="font-family: ${font}; font-style: ${fontStyle}; word-break: break-word;"`)
      .replace(/<h1/g, `<h1 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<h2/g, `<h2 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<h3/g, `<h3 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<h4/g, `<h4 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<h5/g, `<h5 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<h6/g, `<h6 style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${headingSize}; word-break: break-word; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<li/g, `<li style="font-family: ${font}; font-style: ${fontStyle}; font-size: ${fontSize}; line-height: ${lineSpacing}px; word-break: break-word; margin-bottom: ${paragraphSpacing / 2}px;"`)
      .replace(/<ul/g, `<ul style="font-family: ${font}; font-style: ${fontStyle}; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<ol/g, `<ol style="font-family: ${font}; font-style: ${fontStyle}; margin-bottom: ${paragraphSpacing}px;"`)
      .replace(/<td/g, `<td style="font-family: ${font}; font-style: ${fontStyle}; word-break: break-word; padding: 8px;"`)
      .replace(/<th/g, `<th style="font-family: ${font}; font-style: ${fontStyle}; word-break: break-word; padding: 8px;"`)
      .replace(/<tr/g, `<tr style="font-family: ${font}; font-style: ${fontStyle};"`)
      .replace(/<table/g, `<table style="font-family: ${font}; font-style: ${fontStyle}; width: 100%; table-layout: fixed; margin-bottom: ${paragraphSpacing}px;"`);
  };

  // ─── SECTION RENDERERS ───────────────────────────────────────────────────────

  const renderProfileSection = () => (
    <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
      <SidebarSectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>
        PROFILE
      </SidebarSectionHeader>
      <Box sx={{ mt: 2 }}>
        <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>
          <LocationOnIcon />
          <Typography sx={getTextStyle()}>{data.city}</Typography>
        </InfoItem>
        <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>
          <PhoneIcon />
          <Typography sx={getTextStyle()}>{data.phone}</Typography>
        </InfoItem>
        <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>
          <EmailIcon />
          <Typography sx={getTextStyle()}>{data.email}</Typography>
        </InfoItem>
        {data.linkedIn && (
          <InfoItem color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>
            <LinkedInIcon />
            <Typography sx={getTextStyle()}>{data.linkedIn}</Typography>
          </InfoItem>
        )}
      </Box>
    </Box>
  );

  const renderEducationSection = () => (
    <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
      <SidebarSectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>
        EDUCATION
      </SidebarSectionHeader>
      <Box sx={{ mt: 2 }}>
        {educationEntries.length > 0 ? (
          educationEntries.map((edu, index) => {
            if (edu.educationLevel) {
              if (edu.educationLevel === "school") {
                return (
                  <TimelineItem key={index}>
                    <TimelineIcon color={palette.accentColor}><SchoolIcon /></TimelineIcon>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {`${edu.standard} Standard`} {edu.stream ? `- ${edu.stream}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.percentage ? `Percentage: ${edu.percentage}%` : ''}
                      </Typography>
                    </Box>
                  </TimelineItem>
                );
              } else {
                return (
                  <TimelineItem key={index}>
                    <TimelineIcon color={palette.accentColor}><SchoolIcon /></TimelineIcon>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="caption" sx={{ color: palette.lightText, display: 'block', ...globalFontStyle }}>
                        {formatDate(edu.gradMonth, edu.gradYear)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.degreeName} {edu.specialization ? `- ${edu.specialization}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.collegeName}{edu.collegeLocation ? `, ${edu.collegeLocation}` : ''}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, textIndent: `${paragraphIndent}px`, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.cgpa ? `CGPA: ${edu.cgpa}` : ''}{edu.grade ? ` | Grade: ${edu.grade}` : ''}
                      </Typography>
                      {edu.additionalCoursework && (
                        <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                          {edu.additionalCoursework}
                        </Typography>
                      )}
                    </Box>
                  </TimelineItem>
                );
              }
            } else {
              return (
                <TimelineItem key={index}>
                  <TimelineIcon color={palette.accentColor}><SchoolIcon /></TimelineIcon>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ color: palette.lightText, display: 'block', ...globalFontStyle }}>
                      {formatDate(edu.gradMonth, edu.gradYear)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {edu.degree} {edu.fieldOfStudy ? `- ${edu.fieldOfStudy}` : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                      {edu.schoolName}{edu.schoolLocation ? `, ${edu.schoolLocation}` : ''}
                    </Typography>
                    {edu.additionalCoursework && (
                      <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                        {edu.additionalCoursework}
                      </Typography>
                    )}
                  </Box>
                </TimelineItem>
              );
            }
          })
        ) : (
          <>
            <TimelineItem>
              <TimelineIcon color={palette.accentColor}><SchoolIcon /></TimelineIcon>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: palette.lightText, display: 'block', ...globalFontStyle }}>2014</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word' }}>ENTER YOUR DEGREE</Typography>
                <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle }}>YOUR SCHOOL</Typography>
              </Box>
            </TimelineItem>
            <TimelineItem>
              <TimelineIcon color={palette.accentColor}><SchoolIcon /></TimelineIcon>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: palette.lightText, display: 'block', ...globalFontStyle }}>2016</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: palette.textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word' }}>ENTER YOUR DEGREE</Typography>
                <Typography variant="body2" sx={{ color: palette.textColor, ...globalFontStyle }}>YOUR SCHOOL</Typography>
              </Box>
            </TimelineItem>
          </>
        )}
      </Box>
    </Box>
  );

  const renderSkillsSection = () => (
    <Box className="resume-section">
      <SidebarSectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>SKILLS</SidebarSectionHeader>
      <Box sx={{ mt: 2 }}>
        {savedSkills.length > 0 ? (
          savedSkills.map((skill, index) => (
            <SkillBarWrapper key={index}>
              <SkillLabel color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>
                {skill.name} ({skill.rating || 0}%)
              </SkillLabel>
              <SkillBar color={palette.nameColor} variant="determinate" value={skill.rating || 0} />
            </SkillBarWrapper>
          ))
        ) : (
          <>
            {['YOUR SKILL 1', 'YOUR SKILL 2', 'YOUR SKILL 3', 'YOUR SKILL 4'].map((s, i) => (
              <SkillBarWrapper key={i}>
                <SkillLabel color={palette.accentColor} font={font} fontSize={fontSize} fontStyle={fontStyle} lineSpacing={lineSpacing}>{s}</SkillLabel>
                <SkillBar color={palette.nameColor} variant="determinate" value={[90, 80, 95, 75][i]} />
              </SkillBarWrapper>
            ))}
          </>
        )}
      </Box>
    </Box>
  );

  // Render right section content
  const renderRightSectionContent = (sectionId) => {
    const textColor = palette.textColor;
    const SectionHeader = MainContentSectionHeader;

    switch (sectionId) {
      case 'aboutMe':
        return (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>ABOUT ME</SectionHeader>
            <Typography 
              variant="body2" 
              sx={{ 
                color: textColor, 
                lineHeight: `${lineSpacing}px`,
                fontFamily: font,
                fontSize: fontSize,
                fontStyle: fontStyle,
                textIndent: `${paragraphIndent}px`, 
                wordBreak: 'break-word', 
                overflowWrap: 'break-word',
                marginBottom: `${paragraphSpacing}px`
              }}
            >
              {savedSummary || data.profile}
            </Typography>
          </Box>
        );

      case 'experience':
        return (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>PROFESSIONAL EXPERIENCE</SectionHeader>
            {workExperiences && workExperiences.length > 0 ? (
              workExperiences.map((work, index) => (
                <Box key={index} sx={{ mb: sectionSpacing / 10 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: textColor, fontFamily: font, fontSize: getFontSize(1.1), fontStyle, lineHeight: `${lineSpacing}px`, wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: `${paragraphSpacing / 2}px` }}>
                    {work.jobTitle}
                  </Typography>
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: palette.lightText, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: `${paragraphSpacing / 2}px` }}>
                    {work.employer} ({formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)})
                  </Typography>
                  {work.description && (
                    <Box
                      sx={{ 
                        mt: 1, 
                        ...globalFontStyle, 
                        color: textColor, 
                        textIndent: `${paragraphIndent}px`, 
                        wordBreak: 'break-word', 
                        overflowWrap: 'break-word',
                        "& strong": { fontWeight: "bold", fontFamily: font, fontStyle, fontSize, lineHeight: `${lineSpacing}px` }, 
                        "& em": { fontStyle: "italic", fontFamily: font, fontSize, lineHeight: `${lineSpacing}px` }, 
                        "& u": { textDecoration: "underline", fontFamily: font, fontStyle, fontSize, lineHeight: `${lineSpacing}px` },
                        "& p": { marginBottom: `${paragraphSpacing}px` }
                      }}
                      dangerouslySetInnerHTML={{
                        __html: work.description
                          .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-weight: bold; font-style: ${fontStyle}; font-size: ${fontSize}; line-height: ${lineSpacing}px">$1</strong>`)
                          .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-style: italic; font-size: ${fontSize}; line-height: ${lineSpacing}px">$1</em>`)
                          .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px">$1</u>`)
                          .replace(/\n/g, `<br />`),
                      }}
                    />
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: textColor, lineHeight: `${lineSpacing}px`, ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: `${paragraphSpacing}px` }}>
                No work experiences added yet.
              </Typography>
            )}
          </Box>
        );

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>LANGUAGES</SectionHeader>
            {savedLanguages.map((lang, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {lang.name} {lang.level ? `– ${lang.level}` : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>ACCOMPLISHMENTS</SectionHeader>
            {savedAccomplishments.map((acc, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }} whiteSpace="pre-line">
                {acc}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>CERTIFICATIONS</SectionHeader>
            {savedCertifications.map((cert, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {cert.name} – {cert.provider} ({cert.year})
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'softwareSkills':
        return softwareSkills.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>SOFTWARE SKILLS</SectionHeader>
            {softwareSkills.map((skill, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {skill.name} — {skill.level}%
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>VOLUNTEERING</SectionHeader>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: sectionSpacing / 10 }}>
                <Typography sx={{ fontSize: getFontSize(1.1), fontWeight: "bold", ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{vol.subtopic}</Typography>
                <Typography sx={{ fontSize, color: "gray", ...globalFontStyle, wordBreak: 'break-word', overflowWrap: 'break-word', marginBottom: `${paragraphSpacing / 2}px` }}>{vol.fromDate} - {vol.toDate}</Typography>
                <Typography sx={{ fontSize, whiteSpace: "pre-line", ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>INTERESTS</SectionHeader>
            {savedInterests.map((interest, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>• {interest}</Typography>
            ))}
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>WEBSITES / PROFILES</SectionHeader>
            {savedWebsites.map((site, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                <a href={site.url.startsWith('http') ? site.url : `https://${site.url}`} target="_blank" rel="noopener noreferrer" style={{ color: palette.accentColor, textDecoration: 'none', wordBreak: 'break-all' }}>
                  {site.name || site.url}
                </a>
                {site.addToHeader ? " (Shown in Header)" : ""}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>REFERENCES</SectionHeader>
            {savedReferences.map((ref, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }} whiteSpace="pre-line">
                {ref}
              </Typography>
            ))}
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
            <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>ADDITIONAL INFORMATION</SectionHeader>
            {savedAdditionalInfo.map((info, index) => (
              <Typography key={index} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }}>{info}</Typography>
            ))}
          </Box>
        ) : null;

      default:
        if (sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          return items.length > 0 ? (
            <Box sx={{ mb: sectionSpacing / 10 }} className="resume-section">
              <SectionHeader color={palette.nameColor} font={font} fontSize={headingSize}>
                {customSectionName.toUpperCase()}
              </SectionHeader>
              {items.map((item, i) => (
                <Typography key={i} sx={{ mb: `${paragraphSpacing / 10}px`, ...globalFontStyle, color: textColor, wordBreak: 'break-word', overflowWrap: 'break-word' }} whiteSpace="pre-line">
                  {item}
                </Typography>
              ))}
            </Box>
          ) : null;
        }
        return null;
    }
  };

  // Render left section content - supports sections moved from right side
  const renderLeftSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'profile': return renderProfileSection();
      case 'education': return renderEducationSection();
      case 'skills': return renderSkillsSection();
      default: return renderRightSectionContent(sectionId);
    }
  };

  // ─── PAGINATION: Only paginate the RIGHT (main) content ──────────────────────
  // We measure the right-side content sections and split across pages.
  // The LEFT sidebar is ALWAYS rendered on every page at full height.

  const paginateContent = useCallback(() => {
    if (!rightContentRef.current) return;

    const mainSections = Array.from(rightContentRef.current.querySelectorAll('.resume-section'));
    const pageHeightPx = parseFloat(page.height.replace('mm', '')) * 3.7795275591;
    // Reserve space: header (~130px, only page 1), footer (30px), vertical padding, top/bottom margins
    const firstPageAvailableHeight = pageHeightPx - 130 - 30 - (topBottomMargin * 2) - 80;
    const otherPageAvailableHeight = pageHeightPx - 30 - (topBottomMargin * 2) - 80;

    let newPages = [];
    let currentPageSections = [];
    let currentHeight = 0;
    let isFirstPage = true;

    if (mainSections.length === 0) {
      setPages([{ sections: [], isFirstPage: true }]);
      return;
    }

    mainSections.forEach((sectionEl) => {
      const sectionHeight = sectionEl.offsetHeight + (sectionSpacing / 10 * 8);
      const availableHeight = isFirstPage ? firstPageAvailableHeight : otherPageAvailableHeight;

      if (currentHeight + sectionHeight > availableHeight && currentPageSections.length > 0) {
        newPages.push({ sections: currentPageSections, isFirstPage });
        isFirstPage = false;
        currentPageSections = [sectionEl];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(sectionEl);
        currentHeight += sectionHeight;
      }
    });

    if (currentPageSections.length > 0) {
      newPages.push({ sections: currentPageSections, isFirstPage });
    }

    const serializedPages = newPages.map(p => ({
      sections: p.sections.map(s => s.outerHTML),
      isFirstPage: p.isFirstPage
    }));
    setPages(serializedPages);
  }, [page.height, topBottomMargin, sectionSpacing, font, fontSize, fontStyle, lineSpacing, paragraphIndent]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      paginateContent();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [
    paginateContent,
    educationEntries, savedSkills, savedSummary, workExperiences,
    savedAdditionalInfo, savedLanguages, savedAccomplishments,
    savedCertifications, savedReferences, softwareSkills,
    savedVolunteering, savedInterests, savedWebsites, customSections,
    leftSectionOrder, rightSectionOrder,
    font, fontSize, fontStyle, lineSpacing, paragraphIndent, sectionSpacing,
  ]);

  // Hidden measurement container for right-side sections only
  const renderRightContentToMeasure = () => (
    <Box
      ref={rightContentRef}
      sx={{
        visibility: 'hidden',
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        width: '65%',
        fontFamily: font,
        p: `${topBottomMargin}px ${sideMargins}px`,
        overflow: 'hidden',
      }}
    >
      {rightSectionOrder.map(sectionId => (
        <React.Fragment key={sectionId}>
          {renderRightSectionContent(sectionId)}
        </React.Fragment>
      ))}
    </Box>
  );

  // ─── SIDEBAR - Always rendered on EVERY page ──────────────────────────────────
  const renderSidebar = () => (
    <Box
      sx={{
        width: '35%',
        flexShrink: 0,
        p: 4,
        borderRight: '1px solid #eee',
        backgroundColor: palette.sidebarBackground,
        fontFamily: font,
        fontSize: fontSize,
        fontStyle: fontStyle,
        lineHeight: `${lineSpacing}px`,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {leftSectionOrder.map((sectionId, index) => (
        <React.Fragment key={sectionId}>
          {renderLeftSectionContent(sectionId)}
          {index < leftSectionOrder.length - 1 && (
            <Divider sx={{ my: sectionSpacing / 10, borderColor: '#eee', borderBottomWidth: lineWeight }} />
          )}
        </React.Fragment>
      ))}
    </Box>
  );

  // Sidebar for subsequent pages (empty, just for layout consistency)
  const renderEmptySidebar = () => (
    <Box
      sx={{
        width: '35%',
        flexShrink: 0,
        borderRight: '1px solid #eee',
        backgroundColor: palette.sidebarBackground,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    />
  );

  // ─── MAIN CONTENT for a given page ───────────────────────────────────────────
  const renderMainContent = (pageContent) => (
    <Box
      sx={{
        width: '65%',
        p: 4,
        fontFamily: font,
        fontSize: fontSize,
        fontStyle: fontStyle,
        lineHeight: `${lineSpacing}px`,
        overflow: 'hidden',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          fontFamily: font,
          fontStyle: fontStyle,
          fontSize: fontSize,
          lineHeight: `${lineSpacing}px`,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          overflow: 'hidden',
          maxWidth: '100%',
        }}
        dangerouslySetInnerHTML={{
          __html: addFontStylesToHTML(
            pageContent.sections.join('')
          )
        }}
      />
    </Box>
  );

  // ─── PAGE WRAPPER ─────────────────────────────────────────────────────────────
  const renderPage = (pageContent, pageIndex) => {
    const isFirstPage = pageIndex === 0;
    const isVisible = exporting || pageIndex === currentPage;

    return (
      <Box
        key={pageIndex}
        className="resume-page"
        sx={{
          width: page.width,
          minHeight: page.height,
          mx: "auto",
          bgcolor: "#fff",
          boxShadow: pageIndex === currentPage ? 3 : 0,
          fontFamily: font,
          fontSize: fontSize,
          fontStyle: fontStyle,
          lineHeight: `${lineSpacing}px`,
          display: isVisible ? "flex" : "none",
          flexDirection: "column",
          pageBreakAfter: "always",
          overflow: 'hidden',
          "@media print": {
            display: 'flex',
            width: page.width,
            height: "auto",
            minHeight: page.height,
            boxShadow: "none",
            m: 0,
          },
          border: `${lineWeight}px solid ${palette.accentColor}`,
          my: topBottomMargin / 10,
          p: `${topBottomMargin / 2}px ${sideMargins / 2}px`,
          boxSizing: 'border-box',
        }}
      >
        {/* Header - ONLY on first page */}
        {isFirstPage && (
          <HeaderBackground color={palette.header}>
            <NameTitle color={palette.nameColor} font={font} fontSize={headingSize}>
              {data.firstName} {data.lastName}
            </NameTitle>
            <RoleTitle color={palette.nameColor} font={font} fontSize={getFontSize(1.1)}>
              {data.currentPosition}
            </RoleTitle>
          </HeaderBackground>
        )}

        {/* Two-column layout: sidebar (35%) + main content (65%) - ON EVERY PAGE */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Sidebar: full content on page 1, empty column on subsequent pages */}
          {isFirstPage ? renderSidebar() : renderEmptySidebar()}
          {/* Main content */}
          {renderMainContent(pageContent)}
        </Box>

        {/* Footer - on all pages */}
        <Box sx={{
          height: '30px',
          backgroundColor: palette.header,
          borderTop: `${lineWeight}px solid ${palette.accentColor}`,
          flexShrink: 0,
        }} />
      </Box>
    );
  };

  // Fallback single page when pagination hasn't run yet
  const renderFallbackPage = () => (
    <Box
      className="resume-page"
      sx={{
        width: page.width,
        minHeight: page.height,
        mx: "auto",
        bgcolor: "#fff",
        boxShadow: 3,
        fontFamily: font,
        fontSize: fontSize,
        fontStyle: fontStyle,
        lineHeight: `${lineSpacing}px`,
        display: "flex",
        flexDirection: "column",
        overflow: 'hidden',
        "@media print": { display: 'flex', width: page.width, height: "auto", minHeight: page.height, boxShadow: "none", m: 0 },
        border: `${lineWeight}px solid ${palette.accentColor}`,
        my: topBottomMargin / 10,
        p: `${topBottomMargin / 2}px ${sideMargins / 2}px`,
        boxSizing: 'border-box',
      }}
    >
      <HeaderBackground color={palette.header}>
        <NameTitle color={palette.nameColor} font={font} fontSize={headingSize}>
          {data.firstName} {data.lastName}
        </NameTitle>
        <RoleTitle color={palette.nameColor} font={font} fontSize={getFontSize(1.1)}>
          {data.currentPosition}
        </RoleTitle>
      </HeaderBackground>

      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {renderSidebar()}
        <Box sx={{ width: '65%', p: 4, fontFamily: font, fontSize, fontStyle, lineHeight: `${lineSpacing}px`, overflow: 'hidden', minWidth: 0, boxSizing: 'border-box' }}>
          {rightSectionOrder.map(sectionId => (
            <React.Fragment key={sectionId}>
              {renderRightSectionContent(sectionId)}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Box sx={{ height: '30px', backgroundColor: palette.header, borderTop: `${lineWeight}px solid ${palette.accentColor}`, flexShrink: 0 }} />
    </Box>
  );

  // Mobile view wrapper - makes resume responsive
  const renderMobileView = () => (
    <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Box sx={{ minWidth: '600px' }}>
        {pages.length > 0
          ? pages.map((pageContent, index) => renderPage(pageContent, index))
          : renderFallbackPage()
        }
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", position: "relative", fontFamily: font }}>
      {/* Pagination controls - hidden on mobile? */}
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary">
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ mx: 2, fontFamily: font }}>
            Page {currentPage + 1} of {Math.max(1, pages.length)}
          </Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage >= Math.max(0, pages.length - 1)} color="primary">
            <ChevronRight />
          </IconButton>

          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary" size="small">
                Arrange Sections
              </Button>
              <Button onClick={resetAllSectionOrder} variant="outlined" color="secondary" size="small">
                Reset All Sections
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Hidden measurement container */}
      {renderRightContentToMeasure()}

      {/* Resume pages - responsive wrapper */}
      <Box id="resume-container" sx={{ 
        fontFamily: font,
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}>
        <Box sx={{ 
          minWidth: { xs: '600px', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          {pages.length > 0
            ? pages.map((pageContent, index) => renderPage(pageContent, index))
            : renderFallbackPage()
          }
        </Box>
      </Box>

      {/* Arrange Sections Dialog */}
      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Reorder sx={{ mr: 1 }} />
            Arrange Resume Sections
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop sections to reorder them within each side or move sections between left and right sides.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, width: "100%" }}>
            <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>{renderDragPanel("left", leftSectionOrder)}</Box>
            <Box sx={{ flex: 1, width: { xs: "100%", md: "50%" } }}>{renderDragPanel("right", rightSectionOrder)}</Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAllSectionOrder} color="secondary">Reset All</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add metadata for filtering
export const resumeMeta = {
  hasPhoto: false,
  columns: 2,
  colorOptions: [
    { value: '#f0f0f0', label: 'Light Gray' },
    { value: '#e3f2fd', label: 'Light Blue' },
    { value: '#f1f8e9', label: 'Light Green' },
    { value: '#ffebee', label: 'Light Pink' },
    { value: '#fff3e0', label: 'Light Orange' }
  ],
  nameColorOptions: [
    { value: '#333', label: 'Dark Gray' },
    { value: '#1565c0', label: 'Blue' },
    { value: '#33691e', label: 'Green' },
    { value: '#c62828', label: 'Red' },
    { value: '#e65100', label: 'Orange' }
  ],
  sidebarBackgroundOptions: [
    { value: '#fff', label: 'White' },
    { value: '#f8f9fa', label: 'Light Gray' },
    { value: '#e3f2fd', label: 'Very Light Blue' },
    { value: '#f3e5f5', label: 'Very Light Purple' },
    { value: '#e8f5e9', label: 'Very Light Green' }
  ],
  headerBackgroundOptions: [
    { value: '#f0f0f0', label: 'Light Gray' },
    { value: '#e3f2fd', label: 'Light Blue' },
    { value: '#f1f8e9', label: 'Light Green' },
    { value: '#ffebee', label: 'Light Pink' },
    { value: '#fff3e0', label: 'Light Orange' }
  ],
  accentColorOptions: [
    { value: '#777', label: 'Medium Gray' },
    { value: '#1976d2', label: 'Blue' },
    { value: '#689f38', label: 'Green' },
    { value: '#d32f2f', label: 'Red' },
    { value: '#f57c00', label: 'Orange' }
  ],
  experienceLevel: ["No Experience", "Less than 3 years", "3-5 Years", "5-10 Years", "10+ Years"]
};

export default Resume21;